/**
 * A backup plan's action menu: a kebab trigger, one item today ("Clear run
 * history"). Built on the same accessible-dropdown shape as
 * `marketing/ProfileMenu.tsx` — `aria-haspopup`/`aria-expanded`,
 * `role="menu"`/`menuitem`, close on blur or Escape — rather than as a
 * one-off, since a second action is only a matter of time.
 *
 * Choosing the item swaps the panel to an inline confirm rather than acting
 * immediately: the portal has no modal component, and the established
 * pattern for anything destructive here (`pages/Overview.tsx`'s
 * `RemoveMachineButton`) is a second click still on the same control, not a
 * dialog that can be dismissed on autopilot.
 */

import { useRef, useState, type FocusEvent, type KeyboardEvent } from "react";

type Phase = "menu" | "confirming" | "working" | "failed";

interface Props {
  machineId: string;
  jobId: string;
  /** What the trigger's `aria-label` names — e.g. a job's display name. */
  label: string;
  onClearHistory: (machineId: string, jobId: string) => Promise<void>;
}

export function JobActionsMenu({ machineId, jobId, label, onClearHistory }: Props) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>("menu");
  const [error, setError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  function close() {
    setOpen(false);
    setPhase("menu");
    setError(null);
  }

  // Closes once focus leaves the menu for good — a click elsewhere, or
  // Tabbing out — rather than tracking outside clicks by hand.
  function onBlur(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget)) close();
  }

  // Escape closes and returns focus to the trigger, so keyboard use doesn't
  // lose its place.
  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      close();
      triggerRef.current?.focus();
    }
  }

  async function confirm() {
    setPhase("working");
    try {
      await onClearHistory(machineId, jobId);
      close();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not clear this job's history.");
      setPhase("failed");
    }
  }

  return (
    <div className="job-actions-menu" onBlur={onBlur} onKeyDown={onKeyDown}>
      <button
        type="button"
        ref={triggerRef}
        className="job-actions-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Actions for ${label}`}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <span aria-hidden="true">⋯</span>
      </button>

      {open && (
        <div className="job-actions-menu__panel" role="menu">
          {phase === "menu" && (
            <button
              type="button"
              role="menuitem"
              className="job-actions-menu__item"
              onClick={() => setPhase("confirming")}
            >
              Clear run history
            </button>
          )}

          {phase === "confirming" && (
            <div className="job-actions-menu__confirm">
              <p>
                Clear history? Its runs leave the portal. A plan that's still
                installed repopulates them on its next run.
              </p>
              <div className="job-actions-menu__confirm-actions">
                <button
                  type="button"
                  className="button button--small button--danger"
                  onClick={confirm}
                >
                  Confirm
                </button>
                <button
                  type="button"
                  className="button button--small"
                  onClick={() => setPhase("menu")}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {phase === "working" && <p className="muted job-actions-menu__status">Clearing…</p>}

          {phase === "failed" && (
            <div className="job-actions-menu__confirm">
              <p className="muted">{error}</p>
              <button
                type="button"
                className="button button--small"
                onClick={() => setPhase("confirming")}
              >
                Try again
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
